import {
  GetSinglePriolistRequest,
  GetChecklistRequest,
  GetCompanySummaryRequest,
} from '@its4plan/pdf_service';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useApiContext } from '~/common/api/common/apiCommons';

/**
 * Function to fetch the Priolist PDF for a specific Prio
 */
export function useGetSinglePriolistPdf<T = Blob>(
  options?: UseQueryOptions<Blob, Error, T> & {
    request?: GetSinglePriolistRequest;
  },
) {
  const { singlePriolistPdfApi } = useApiContext();

  return useQuery({
    queryKey: ['priolist', 'pdf', options?.request?.prio],
    queryFn: () => singlePriolistPdfApi.getSinglePriolist(options!.request!),
    enabled: !!options?.request,
    ...options,
  });
}

/**
 * Function to fetch the Priolist PDF for all Prios
 */
export function useGetFullPriolistPdf() {
  const { fullPriolistPdfApi } = useApiContext();

  return useQuery({
    queryKey: ['priolist', 'pdf', 'full'],
    queryFn: () => fullPriolistPdfApi.getFullPriolist(),
    enabled: false,
  });
}

/**
 * Function to fetch the Checklist PDF for a specific project
 */
export function useGetChecklistPdf<T = Blob>(
  options?: UseQueryOptions<Blob, Error, T> & {
    request?: GetChecklistRequest;
  },
) {
  const { checklistApi } = useApiContext();

  return useQuery({
    queryKey: ['checklist', 'pdf', options?.request?.project],
    queryFn: () => checklistApi.getChecklist(options!.request!),
    enabled: !!options?.request,
    ...options,
  });
}

/**
 * Hook to fetch the Company Summary PDF
 */
export function useGetCompanySummaryPdf<T = Blob>(
  options?: UseQueryOptions<Blob, Error, T> & {
    request?: GetCompanySummaryRequest;
  },
) {
  const { companySummaryApi } = useApiContext();

  return useQuery<Blob, Error, T>({
    queryKey: [
      'company',
      'summary',
      'pdf',
      options?.request?.startDate,
      options?.request?.endDate,
      options?.request?.additionalHours,
      options?.request?.employeeIds,
    ],
    queryFn: () => {
      if (!options?.request) {
        throw new Error('Start date and end date are required');
      }
      return companySummaryApi.getCompanySummary(options.request);
    },
    enabled: !!options?.request,
    ...options,
  });
}